/**
 * RTK Query endpoints for the webhook engine (@cluster/communication).
 * Endpoints are injected into the shared `apiSlice` so they share the same
 * baseQuery (token refresh) and cache tag space. Base path after the dev
 * proxy + nginx rewrite strips `/api/communication`.
 */
import { apiSlice } from './apiSlice';
import type {
  Webhook,
  CreateWebhookDto,
  UpdateWebhookDto,
  SandboxTestResult,
  CodeValidationResult,
  DeliveryLog,
  PaginatedResponse,
} from '@/types';

// Cache tags — list + per-item for webhooks, list + per-item for delivery logs.
const WEBHOOK_LIST = { type: 'Webhook' as const, id: 'LIST' };
const LOG_LIST = { type: 'WebhookLog' as const, id: 'LIST' };

export interface WebhookListParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface DeliveryLogsParams {
  webhookId: string;
  page?: number;
  limit?: number;
}

export const communicationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Webhooks ──────────────────────────────────────────────────────────
    getWebhooks: builder.query<PaginatedResponse<Webhook>, WebhookListParams | void>({
      query: (params) => ({
        url: '/communication/webhooks',
        params: params || undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              WEBHOOK_LIST,
              ...result.data.map(({ _id, id }) => ({ type: 'Webhook' as const, id: id || _id })),
            ]
          : [WEBHOOK_LIST],
    }),

    getWebhook: builder.query<Webhook, string>({
      query: (webhookId) => `/communication/webhooks/${webhookId}`,
      transformResponse: (response: { data: Webhook }) => response.data,
      providesTags: (_result, _error, webhookId) => [{ type: 'Webhook', id: webhookId }],
    }),

    createWebhook: builder.mutation<Webhook, CreateWebhookDto>({
      query: (body) => ({ url: '/communication/webhooks', method: 'POST', body }),
      transformResponse: (response: { data: Webhook }) => response.data,
      invalidatesTags: [WEBHOOK_LIST],
    }),

    updateWebhook: builder.mutation<Webhook, { id: string; body: UpdateWebhookDto }>({
      query: ({ id, body }) => ({ url: `/communication/webhooks/${id}`, method: 'PATCH', body }),
      transformResponse: (response: { data: Webhook }) => response.data,
      invalidatesTags: (result, _error, { id }) =>
        result ? [{ type: 'Webhook', id }, WEBHOOK_LIST] : [WEBHOOK_LIST],
    }),

    setWebhookEnabled: builder.mutation<Webhook, { id: string; enabled: boolean }>({
      query: ({ id, enabled }) => ({
        url: `/communication/webhooks/${id}/status`,
        method: 'PATCH',
        body: { enabled },
      }),
      transformResponse: (response: { data: Webhook }) => response.data,
      invalidatesTags: (result, _error, { id }) =>
        result ? [{ type: 'Webhook', id }, WEBHOOK_LIST] : [WEBHOOK_LIST],
    }),

    deleteWebhook: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/communication/webhooks/${id}`, method: 'DELETE' }),
      invalidatesTags: [WEBHOOK_LIST],
    }),

    // ── Sandbox / validation (no cache tags) ──────────────────────────────
    runSandboxTest: builder.mutation<SandboxTestResult, { id: string; sampleInput: unknown }>({
      query: ({ id, sampleInput }) => ({
        url: `/communication/webhooks/${id}/test`,
        method: 'POST',
        body: { sampleInput },
      }),
      transformResponse: (response: { data: SandboxTestResult }) => response.data,
    }),

    validateWebhookCode: builder.mutation<CodeValidationResult, { id: string; code: string }>({
      query: ({ id, code }) => ({
        url: `/communication/webhooks/${id}/validate`,
        method: 'POST',
        body: { code },
      }),
      transformResponse: (response: { data: CodeValidationResult }) => response.data,
    }),

    // ── Delivery logs ─────────────────────────────────────────────────────
    getDeliveryLogs: builder.query<PaginatedResponse<DeliveryLog>, DeliveryLogsParams>({
      query: ({ webhookId, page, limit }) => ({
        url: `/communication/webhooks/${webhookId}/logs`,
        params: { page, limit },
      }),
      providesTags: (result) =>
        result
          ? [LOG_LIST, ...result.data.map((log) => ({ type: 'WebhookLog' as const, id: log.id }))]
          : [LOG_LIST],
    }),

    getDeliveryLog: builder.query<DeliveryLog, { webhookId: string; logId: string }>({
      query: ({ webhookId, logId }) => `/communication/webhooks/${webhookId}/logs/${logId}`,
      transformResponse: (response: { data: DeliveryLog }) => response.data,
      providesTags: (_result, _error, { logId }) => [{ type: 'WebhookLog', id: logId }],
    }),
  }),
});

// Export auto-generated hooks (endpoints injected onto the shared apiSlice).
export const {
  useGetWebhooksQuery,
  useGetWebhookQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useSetWebhookEnabledMutation,
  useDeleteWebhookMutation,
  useRunSandboxTestMutation,
  useValidateWebhookCodeMutation,
  useGetDeliveryLogsQuery,
  useGetDeliveryLogQuery,
} = communicationApi;